/// <reference types="vite/client" />

import "./styles.css";

import { AudioController } from "./audio";
import { getAnimalDescriptor, listAnimals } from "./game/animals";
import { METRICS_RECORD, MetricsTracker } from "./metrics";
import {
  DebouncedSaver,
  PersistentStore,
  PROFILE_RECORD,
  type ProfileSnapshot,
} from "./persistence";
import type { ProfileBridge } from "./runGame";
import { StartScreen, type StartScreenAudio } from "./ui";

const PROFILE_AUTOSAVE_MS = 2000;
const METRICS_AUTOSAVE_MS = 2000;
const METRICS_FLUSH_INTERVAL_SECONDS = 5;

interface ProfileSession extends ProfileBridge {
  flush(): void;
}

function createProfileSession(): ProfileSession {
  const store = new PersistentStore(PROFILE_RECORD);
  let current = store.load();
  const saver = new DebouncedSaver(store, { delayMs: PROFILE_AUTOSAVE_MS });
  return {
    snapshot: () => current,
    update(patch: (prev: ProfileSnapshot) => ProfileSnapshot) {
      const next = patch(current);
      if (next === current) return;
      current = next;
      saver.schedule(current);
    },
    flush: () => {
      saver.flush();
    },
  };
}

async function mount(): Promise<void> {
  const root = document.getElementById("app");
  if (!root) {
    throw new Error("Waterra: missing #app mount point");
  }

  const profile = createProfileSession();
  const audio = new AudioController({ enabled: profile.snapshot().settings.audio });
  const metrics = new MetricsTracker(new PersistentStore(METRICS_RECORD), {
    saveDelayMs: METRICS_AUTOSAVE_MS,
    playTimeFlushIntervalSeconds: METRICS_FLUSH_INTERVAL_SECONDS,
  });
  window.addEventListener("pagehide", () => {
    profile.flush();
    metrics.flush();
    metrics.dispose();
    audio.dispose();
  });

  const audioBridge: StartScreenAudio = {
    setEnabled(enabled) {
      audio.setEnabled(enabled);
      profile.update((prev) =>
        prev.settings.audio === enabled
          ? prev
          : { ...prev, settings: { ...prev.settings, audio: enabled } },
      );
    },
    unlock: () => audio.unlock(),
  };

  for (;;) {
    const initial = profile.snapshot();
    const startScreen = new StartScreen(root, {
      animals: listAnimals(),
      initialAnimal: initial.progress.lastAnimal,
      initialSettings: initial.settings,
      audio: audioBridge,
      onReplayTutorial: () => {
        profile.update((prev) =>
          prev.onboarding.completed ? { ...prev, onboarding: { completed: false } } : prev,
        );
      },
    });
    const choice = await startScreen.selection();
    startScreen.dispose();

    profile.update((prev) => {
      const sameAnimal = prev.progress.lastAnimal === choice.animal;
      const sameSettings =
        prev.settings.audio === choice.settings.audio &&
        prev.settings.controls === choice.settings.controls;
      if (sameAnimal && sameSettings) return prev;
      return {
        ...prev,
        progress: { ...prev.progress, lastAnimal: choice.animal },
        settings: choice.settings,
      };
    });

    audio.setEnabled(choice.settings.audio);
    const animal = getAnimalDescriptor(choice.animal);
    metrics.recordSessionStart(choice.animal);
    const { runGame } = await import("./runGame");
    await runGame({ root, animal, profile, audio, metrics });
  }
}

void mount();
